import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import { CalcCard, CalcField, CalcRow, CalcStat, usd } from "@/components/site/CalcKit";
import { CalculatorScaffold } from "@/components/site/CalculatorScaffold";
import prepaymentPenaltyHero from "@/assets/hero/hero-prepayment-penalty-exit-timing.jpg";
import { buildHead } from "@/lib/seo-head";
import { useShareableCalcState } from "@/lib/calc-share";
import { trackCalcEstimateViewed, trackCalcInput } from "@/lib/calculator-analytics";
import {
  monthlyPayment,
  prepayPenaltyCost,
  prepayPenaltyPercent,
  remainingBalance,
} from "@/lib/investor-calcs";

const FAQS = [
  {
    q: "What is a prepayment penalty on a DSCR loan?",
    a: "A fee charged if the loan pays off — through sale or refinance — inside a defined window. Business-purpose investor loans commonly use a step-down such as 5/4/3/2/1, meaning 5% in year one falling to 1% in year five.",
  },
  {
    q: "How is the penalty calculated?",
    a: "The applicable percentage is multiplied by a balance basis defined in the note — usually the outstanding balance at payoff, sometimes the original loan amount. This tool lets you choose the basis so you can model both.",
  },
  {
    q: "Can I buy out the prepayment penalty?",
    a: "Often yes. Programs typically price a shorter penalty term or no penalty with a rate or points adjustment. Compare the buy-out cost to the penalty you would actually pay at your planned exit year.",
  },
  {
    q: "Do prepayment penalties apply if I sell?",
    a: "It depends on the note. Some investor notes waive the penalty on a bona fide sale to an unrelated party and enforce it only on a refinance. Read the prepayment rider before assuming either way.",
  },
  {
    q: "Are prepayment penalties allowed in every state?",
    a: "Enforceability and limits vary by state and by whether the loan is business-purpose. Program availability and terms differ accordingly; a licensed loan officer can confirm what applies to your scenario.",
  },
  {
    q: "What does a 5/4/3/2/1 step-down mean in dollars?",
    a: "It means the penalty percentage drops each year the loan is in force: 5% of the balance basis in year one, 4% in year two, and so on down to 1% in year five before disappearing. On a $300,000 payoff balance, a year-two payoff at 4% would cost roughly $12,000.",
  },
  {
    q: "Is a shorter prepayment penalty term always better?",
    a: "Not automatically — programs often charge a rate or fee premium for a shorter penalty term or none at all, so a 3-year step-down might cost more upfront than a 5-year one. Compare the pricing trade-off against your realistic hold period before choosing.",
  },
  {
    q: "Does refinancing with the same lender waive the penalty?",
    a: "Rarely by default; most notes apply the penalty regardless of which lender the new loan comes from unless the note specifically carves out an in-house refinance exception. Read the prepayment rider rather than assuming a waiver.",
  },
  {
    q: "What is yield maintenance and how does it differ from step-down?",
    a: "Yield maintenance charges a penalty tied to the lender's lost interest income relative to current rates, which can be far larger than a fixed step-down percentage, especially when rates have fallen since closing. Step-down penalties are simpler and generally more predictable for investors to plan around.",
  },
  {
    q: "Does a partial paydown trigger the prepayment penalty?",
    a: "It depends on the note — some programs only penalize a full payoff, while others apply the penalty to partial curtailments above a stated annual threshold, commonly 20%. Check the specific note language before making a large extra principal payment.",
  },
  {
    q: "How do I estimate the penalty before I even close?",
    a: "Ask the loan officer for the exact step-down schedule and balance basis in writing before signing disclosures, then run your planned exit year through this calculator to see the estimated dollar cost. Comparing that cost against a shorter-penalty option's pricing adjustment helps you pick the better structure.",
  },
  {
    q: "Are prepayment penalties negotiable at closing?",
    a: "The term length and whether one applies at all are often chosen from a menu of pricing options rather than fixed, so you may be able to select a shorter term or none in exchange for a pricing adjustment. Ask what options the program offers rather than assuming the initial quote is the only one available.",
  },
];

export const Route = createFileRoute("/prepayment-penalty-calculator")({
  head: () =>
    buildHead({
      title: "Prepayment Penalty Calculator (5/4/3/2/1) | Simply Approved",
      description:
        "Model step-down and fixed prepayment penalties on a DSCR or investor loan. See the dollar cost by exit year and whether buying the penalty down pays off.",
      url: "/prepayment-penalty-calculator",
      keywords:
        "prepayment penalty calculator, DSCR prepayment penalty, 5/4/3/2/1 step down penalty, prepay buyout cost, investor loan prepayment penalty, yield maintenance vs step down, exit year penalty calculator",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Prepayment Penalty Calculator", url: "/prepayment-penalty-calculator" },
      ],
      service: { name: "Prepayment Penalty Calculator" },
      faqs: FAQS,
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Prepayment Penalty Calculator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/prepayment-penalty-calculator",
          description: "Step-down and fixed prepayment penalty cost calculator for investor loans.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: PrepayPage,
});

type Inputs = {
  loanAmount: number;
  ratePct: number;
  termYears: number;
  useCurrentBalance: number;
  y1: number;
  y2: number;
  y3: number;
  y4: number;
  y5: number;
  exitYear: number;
  buyoutRateAddPct: number;
};

const DEFAULT: Inputs = {
  loanAmount: 350000,
  ratePct: 7.5,
  termYears: 30,
  useCurrentBalance: 1,
  y1: 5,
  y2: 4,
  y3: 3,
  y4: 2,
  y5: 1,
  exitYear: 3,
  buyoutRateAddPct: 0.5,
};

function PrepayPage() {
  const { state: i, patch, shareUrl } = useShareableCalcState<Inputs>("prepayment_penalty", DEFAULT);
  const set = <K extends keyof Inputs>(k: K, v: Inputs[K]) => {
    patch(k, v);
    trackCalcInput("prepayment_penalty", String(k), v as never);
  };

  const m = useMemo(() => {
    const schedule = [i.y1, i.y2, i.y3, i.y4, i.y5];
    const structure = { kind: "stepdown" as const, schedule };
    const rows = [1, 2, 3, 4, 5].map((year) => {
      const bal =
        i.useCurrentBalance >= 1
          ? remainingBalance(i.loanAmount, i.ratePct, i.termYears, year * 12)
          : i.loanAmount;
      return {
        year,
        percent: prepayPenaltyPercent(structure, year),
        basis: bal,
        cost: prepayPenaltyCost(structure, year, bal),
      };
    });
    const exit = rows.find((r) => r.year === Math.min(5, Math.max(1, Math.round(i.exitYear)))) ?? rows[0]!;
    const basePmt = monthlyPayment(i.loanAmount, i.ratePct, i.termYears);
    const buyoutPmt = monthlyPayment(i.loanAmount, i.ratePct + i.buyoutRateAddPct, i.termYears);
    const buyoutMonthly = buyoutPmt - basePmt;
    const buyoutCostToExit = buyoutMonthly * Math.max(1, Math.round(i.exitYear)) * 12;
    return { rows, exit, basePmt, buyoutPmt, buyoutMonthly, buyoutCostToExit };
  }, [i]);

  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    trackCalcEstimateViewed("prepayment_penalty", {
      exit_year: i.exitYear,
      penalty_cost: Math.round(m.exit.cost),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buyoutWins = m.buyoutCostToExit < m.exit.cost;

  const aiFacts = [
    { label: "Loan amount", value: usd(i.loanAmount) },
    { label: "Step-down schedule", value: `${i.y1}/${i.y2}/${i.y3}/${i.y4}/${i.y5}` },
    { label: "Planned exit year", value: String(Math.round(i.exitYear)) },
    { label: "Penalty at exit", value: usd(m.exit.cost) },
    { label: "Penalty rate at exit", value: `${m.exit.percent}%` },
    { label: "Buy-out rate add-on", value: `${i.buyoutRateAddPct}%` },
    { label: "Buy-out cost to exit", value: usd(m.buyoutCostToExit) },
  ];

  return (
    <CalculatorScaffold
      calculator="prepayment_penalty"
      eyebrow="Prepayment penalty"
      title="Prepayment Penalty Calculator for Investor Loans"
      subtitle="Model a step-down prepay, see the dollar cost at your planned exit year, and compare it against paying a higher rate for a shorter penalty. Illustrative estimates only — your note controls."
      heroImage={{
        src: prepaymentPenaltyHero,
        alt: "Rental-property investor reviewing a declining prepayment penalty timeline before a planned sale or refinance",
      }}
      shareUrl={shareUrl}
      shareTitle="My prepayment penalty scenario"
      inputs={
        <>
          <CalcCard title="1. Loan">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Original loan amount" value={i.loanAmount} onChange={(v) => set("loanAmount", v)} prefix="$" />
              <CalcField label="Note rate" value={i.ratePct} onChange={(v) => set("ratePct", v)} suffix="%" step={0.125} hint="Scenario assumption you enter — not a quoted rate." />
              <CalcField label="Amortization (years)" value={i.termYears} onChange={(v) => set("termYears", v)} step={5} />
              <CalcField label="Charge on current balance? (1 = yes)" value={i.useCurrentBalance} onChange={(v) => set("useCurrentBalance", v)} step={1} hint="0 charges the penalty against the original loan amount instead." />
            </div>
          </CalcCard>
          <CalcCard title="2. Step-down schedule (%)">
            <div className="grid gap-4 md:grid-cols-5">
              <CalcField label="Year 1" value={i.y1} onChange={(v) => set("y1", v)} suffix="%" step={1} />
              <CalcField label="Year 2" value={i.y2} onChange={(v) => set("y2", v)} suffix="%" step={1} />
              <CalcField label="Year 3" value={i.y3} onChange={(v) => set("y3", v)} suffix="%" step={1} />
              <CalcField label="Year 4" value={i.y4} onChange={(v) => set("y4", v)} suffix="%" step={1} />
              <CalcField label="Year 5" value={i.y5} onChange={(v) => set("y5", v)} suffix="%" step={1} />
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Set later years to 0 to model a 3-year or 2-year structure. A flat penalty is entered as the same
              percentage in each covered year.
            </p>
          </CalcCard>
          <CalcCard title="3. Exit plan & buy-out">
            <div className="grid gap-4 md:grid-cols-2">
              <CalcField label="Planned exit year" value={i.exitYear} onChange={(v) => set("exitYear", v)} step={1} hint="When you expect to sell or refinance." />
              <CalcField label="Rate add-on to shorten/remove prepay" value={i.buyoutRateAddPct} onChange={(v) => set("buyoutRateAddPct", v)} suffix="%" step={0.125} />
            </div>
          </CalcCard>
        </>
      }
      results={
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <CalcStat label={`Penalty in year ${Math.round(i.exitYear)}`} value={usd(m.exit.cost)} colorClass={m.exit.cost > 0 ? "text-destructive" : "text-emerald-600"} />
            <CalcStat label="Penalty rate" value={`${m.exit.percent}%`} />
            <CalcStat label="Buy-out cost to exit" value={usd(m.buyoutCostToExit)} />
            <CalcStat label="Cheaper option" value={buyoutWins ? "Buy the prepay down" : "Keep the prepay"} colorClass="text-brand" />
          </div>
          <div className="rounded-xl border border-border bg-card p-5 shadow-sm">
            <h3 className="font-display text-base font-semibold text-foreground">Cost by exit year</h3>
            <dl className="mt-3 space-y-1.5 text-sm">
              {m.rows.map((r) => (
                <CalcRow
                  key={r.year}
                  label={`Year ${r.year} — ${r.percent}% of ${usd(r.basis)}`}
                  value={usd(r.cost)}
                  strong={r.year === Math.round(i.exitYear)}
                />
              ))}
              <CalcRow label="Payment at note rate" value={usd(m.basePmt)} />
              <CalcRow label={`Payment with +${i.buyoutRateAddPct}%`} value={usd(m.buyoutPmt)} />
              <CalcRow label="Extra per month to buy out" value={usd(m.buyoutMonthly)} />
            </dl>
          </div>
        </div>
      }
      aiFacts={aiFacts}
      aiSuggestions={[
        "Is buying the prepayment penalty down worth it here?",
        "How does a step-down penalty work?",
        "What happens to the penalty if I sell instead of refinance?",
        "Why do investor loans have prepayment penalties at all?",
      ]}
      methodologyIntro="The penalty is a percentage of a balance basis in a covered year — the modeling choice is which basis and which exit year."
      methodology={
        <>
          <p>
            <strong>Step-down.</strong> A declining schedule such as 5/4/3/2/1 charges the year&apos;s percentage on
            payoff during that year. Year one on a $350,000 balance at 5% is $17,500.
          </p>
          <p>
            <strong>Basis matters.</strong> Charging on the current balance produces a smaller figure than the original
            loan amount, and the gap widens with an amortizing loan. Interest-only loans show no difference because the
            balance does not fall.
          </p>
          <p>
            <strong>Buy-out comparison.</strong> A higher rate is paid every month you hold the loan; the penalty is
            paid once at exit. The comparison here totals the extra payment to your exit year against the penalty you
            would otherwise owe — it ignores tax treatment and the time value of money.
          </p>
        </>
      }
      faqs={FAQS}
      faqTitle="Prepayment penalty FAQ"
      related={[
        { to: "/dscr-loan-requirements", label: "DSCR loan requirements" },
        { to: "/dscr-loan-pros-and-cons", label: "DSCR loan pros and cons" },
        { to: "/cash-out-refinance-calculator", label: "Cash-out refinance calculator" },
        { to: "/interest-only-vs-amortizing-calculator", label: "Interest-only vs amortizing" },
        { to: "/dscr-calculator", label: "DSCR calculator" },
        { to: "/rates", label: "See today's rates" },
      ]}
      ctaTitle="Match the prepay to your exit"
      ctaBody="Tell us how long you plan to hold and a licensed loan officer will lay out the prepay structures available for your business-purpose scenario."
    />
  );
}
